import { autoinject, bindable, computedFrom } from "aurelia-framework";
import { TreeNode } from "../tree-node";
import { Property } from "../../../models/property";
import { Api } from "../../../services/api";
import { TreeNodeTypes } from "../../../models/tree-node-types";
import { WebApiResponse } from "../../../models/web-api-response";
import { MdToastService } from "aurelia-materialize-bridge";
import { SplitWordValueConverter } from "../../../converters/split-word";
import { I18N } from "aurelia-i18n";

@autoinject
export class BaseEditor {
	active: boolean = false;
	@bindable propertyName : string;
	dataLength: number = 1;

	api: Api;
	toastService: MdToastService;
	i18n: I18N;

	@bindable treeNode: TreeNode;
	@bindable property: Property;

	@computedFrom("property.id")
	get isNewProperty() {
		return this.property.id === "00000000-0000-0000-0000-000000000000";

	}

	constructor(api: Api, toastService: MdToastService, i18n: I18N) {
		this.api = api;
		this.toastService = toastService;
		this.i18n = i18n;
	}

	activate(model: any) {
		this.treeNode = model.selectedNode;
		this.property = model.property;
		this.propertyName = new SplitWordValueConverter().toView(this.property.name);
	}

	async addOrUpdateProperty(data: string): Promise<WebApiResponse<Property>> {
		this.active = true;
		try {
			const newProperty = <Property>Object.assign({}, this.property);
			newProperty.value = data;
			const response = await this.api.addOrUpdateProperty(this.treeNode.type, this.treeNode.id, newProperty);
			if (response.success) {
				this.toastService.show(this.i18n.tr("data-saved"), 4000, "rounded blue");
				this.property = <Property>Object.assign({}, response.data);
			} else {
				this.toastService.show(`${this.i18n.tr("data-not-saved")}: ${response.message}`, 4000, "rounded blue");
			}

			return response;
		} finally {
			this.active = false;
		}
	}

	async deleteProperty(property: Property): Promise<WebApiResponse<Property>> {
		this.active = true;
		try {
			if (property.id === "00000000-0000-0000-0000-000000000000") {
				const index = this.treeNode.properties.indexOf(this.property);
				this.treeNode.properties.splice(index, 1);
				const response = new WebApiResponse<Property>();
				response.data = <Property>Object.assign({}, this.property);
				response.success = true;
				return response;
			} else {
				const response = await this.api.deleteProperty(this.treeNode.type, this.treeNode.id, property);
				if (response.success) {
					this.toastService.show(this.i18n.tr("property-saved"), 4000, "rounded blue");
					const index = this.treeNode.properties.indexOf(this.property);
					this.treeNode.properties.splice(index, 1);
				} else {
					this.toastService.show(`${this.i18n.tr("property-not-saved")}: ${response.message}`, 4000, "rounded blue");
				}
				return response;
			}
			
		} finally {
			this.active = false;

		}

	}
}

@autoinject
export class BaseRangeRuleEditor extends BaseEditor {

	requiredMinimum: number;
	recommendedMinimum: number;
	recommendedMaximum: number;
	requiredMaximum: number;

	constructor(api: Api, toastService: MdToastService, i18n: I18N) {
		super(api, toastService, i18n);
	}

	activate(model: any) {
		super.activate(model);
		const data = model.property.value.split(",");
		this.dataLength = data.length;
		if (this.dataLength === 4) {
			this.recommendedMinimum = data[0];
			this.requiredMinimum = data[1];
			this.requiredMaximum = data[2];
			this.recommendedMaximum = data[3];
		}

		if (this.dataLength === 2) {
			this.requiredMinimum = data[0];
			this.requiredMaximum = data[1];
		}
	}
}
